
import React, { useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import GlassModal from '../../v2/MUI_custom_component/Modal/GlassModal';
import { HumbeeTabs, CustomButton } from '../../v2/MUI_custom_component';
import { getQuestionConfig } from './config/questionsConfig';
import SingleQuestion from './Components/SingleQuestion';
import MultiQuestionCard from './Components/MultiQuestionCard';
import { BentoBoxGrid, BentoItem } from '../../v2/MUI_custom_component';
import { useTheme } from '@mui/material/styles';
import FieldInput from './Components/FieldInput';
import ProfileCard from '../MyAccount/Profile/Components/ProfileCard';
import { getLanguagesAction, getProfilerAction, SendProfilerAction } from './API/Action';
import { toast } from 'react-toastify';
import Industry from './Cards/Common/Industry';
import { ProfilerQuestionID } from './config/QuestionEnum';

const ActionButtons = ({ onCancel, onSave, hasChanged, loading }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Box sx={{ flex: 1 }}>
      <CustomButton fullWidth variant="outlined" label="Cancel" onClick={onCancel} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <CustomButton fullWidth variant="contained" label={loading ? 'Saving...' : 'Save'} onClick={onSave} disabled={!hasChanged || loading} />
    </Box>
  </Box>
);

const Profiler = () => {
  const loginUser = useSelector((state) => state.loginReducer.loginUser);
  const [sections, setSections] = useState([]);
  const [languageOptions, setLanguageOptions] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editValue, setEditValue] = useState(null);
  const [originalEditValue, setOriginalEditValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchProfilerData = async (event) => {
    setLoading(true);
    const { data, error, message } = await getProfilerAction();
    setLoading(false);
    if (!error) {
      setSections(data);
    } else {
      setSections([]);
    }
  };
  const getLanguage = async (event) => {
    setLoading(true);
    const { data, error, message } = await getLanguagesAction();
    if (!error) {
      setLanguageOptions(data);
    } else {
      setSections(data);
    }
  };
  useEffect(() => {
    fetchProfilerData();
    getLanguage();
  }, []);
  const handleEdit = (question) => {
    setEditQuestion(question);
    let initialValue;
    if (Array.isArray(question.ids)) {
      initialValue = Array.isArray(question.values)
        ? question.values.slice()
        : Array.isArray(question.value)
          ? question.value.slice()
          : [];
    } else {
      initialValue = question.value !== undefined ? question.value : '';
    }
    setEditValue(initialValue);
    setOriginalEditValue(initialValue);
  };
  const handleClose = () => {
    setEditQuestion(null);
    setEditValue(null);
    setOriginalEditValue(null);
    setSubmitLoading(false);
  };
  const handleSave = async () => {
    if (!editQuestion) return;
    let payload;
    if (Array.isArray(editQuestion.ids)) {
      payload = {
        question: editQuestion.ids.map((id, idx) => ({
          id,
          value: Array.isArray(editValue) ? editValue[idx] : editValue
        }))
      };
    } else {
      payload = {
        question: [
          { id: editQuestion.id, value: editValue }
        ]
      };
    }
    setSubmitLoading(true);
    const {data, error, message} = await SendProfilerAction(payload);
    setSubmitLoading(false);
    if (!error) {
      toast.success(message);
      handleClose();
      fetchProfilerData();
    } else {
      toast.error(message);
    }
  };
  const handleSubmitIndustryCard = async (payload) => {
    setSubmitLoading(true);
    const {data, error, message} = await SendProfilerAction(payload);
    setSubmitLoading(false);
    if (!error) {
      toast.success(message);
      fetchProfilerData();
    } else {
      toast.error(message);
    }
  };
  const handleTabChange = (event, newValue) => setTabIndex(newValue);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
   <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
    <ProfileCard
      loginUser={loginUser}
    />
    <Box>
      {Array.isArray(sections) && sections.length > 0 ? (
        <HumbeeTabs
          data={sections}
          currentTab={tabIndex}
          onChange={(_, value) => setTabIndex(value)}
          getTabKey={(_, idx) => idx}
          getTabLabel={(section) => section.section.label}
          tabVariant={ isMobile ? "small" : "medium" }
        />
      ) : null}
      {sections && sections[tabIndex] ? (
        <BentoBoxGrid
          key={sections[tabIndex]?.section?.label}
          column={isMobile ? 1 : 2}
          gap={2}
        >
          {(() => {
            const Questions = (sections[tabIndex] && Array.isArray(sections[tabIndex].questions))
              ? sections[tabIndex].questions
              : [];
            const questions = Questions.map(q => {
              const config = getQuestionConfig(q.id);
              // Inject dynamic options for preferred_language
              let options = config?.options;
              if (config?.fieldType === 'preferred_language' && typeof config?.getOptions === 'function' && languageOptions) {
                options = config.getOptions(languageOptions);
              }
              return {
                ...q,
                config,
                options,
              };
            });
            const rendered = [];
            const seenCategories = new Set();
            questions.sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).forEach(q => {
              const category = q.config?.category;
              if (category) {
                if (seenCategories.has(category)) return;
                seenCategories.add(category);
                const groupQuestions = questions.filter(
                  gq => gq.config?.category === category
                );
                rendered.push(
                  <BentoItem key={category} colSpan={1}>
                    <MultiQuestionCard
                      group={{
                        title: q.title,
                        ids: groupQuestions.map(gq => gq.id),
                        values: groupQuestions.map(gq => gq.value),
                        configs: groupQuestions.map(gq => gq.config),
                        images: groupQuestions.map(gq => gq.config?.image).filter(Boolean),
                        category,
                      }}
                      onEdit={handleEdit}
                    />
                  </BentoItem>
                );
              } else if (q.id === ProfilerQuestionID.INDUSTRY) {
                rendered.push(
                  <BentoItem key={q.id} colSpan={1} >
                    <Industry onSubmit={handleSubmitIndustryCard} width={isMobile ? '100%' : '100%'} maxWidth={'100%'} />
                  </BentoItem>
                );
              } else {
                let options = q.config?.options;
                rendered.push(
                  <BentoItem key={q.id} colSpan={isMobile ? 1 : 1} >
                    <SingleQuestion
                      title={q.title}
                      question={q.config?.question}
                      questionTitle={q.config?.questionTitle}
                      value={q.value}
                      image={q.config?.image}
                      subtitle={q.config?.subtitle || ''}
                      options={options}
                      onEdit={() => handleEdit({ ...q, value: q.value })}
                    />
                  </BentoItem>
                );
              }
            });
            return rendered;
          })()}
        </BentoBoxGrid>
      ) : null}
    </Box>
      <GlassModal
        open={!!editQuestion}
        onClose={handleClose}
        title={editQuestion?.title || ''}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column',}}>
          {editQuestion && (() => {
            const isGroup = Array.isArray(editQuestion.ids);
            const hasChanged = JSON.stringify(editValue) !== JSON.stringify(originalEditValue);

            if (isGroup) {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {editQuestion.ids.map((id, idx) => {
                      const config = editQuestion.configs?.[idx] || getQuestionConfig(id);
                      const currentVal = Array.isArray(editValue) ? editValue[idx] : editValue;

                      return (
                          <Box sx={{ display: 'flex', flexDirection: 'column' }} key={id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {config?.question && (
                                <Typography sx={{ color: '#333', fontSize: '13px', lineHeight: '20px', fontWeight: '700' }} component="legend">
                                  {config?.question}
                                  {config?.questionTitle && (
                                    <Box component='span' sx={{ color: '#8C8C8C', ml: 0.5 }}>
                                      {config?.questionTitle}
                                    </Box>
                                  )}
                                </Typography>
                              )}
                              <FieldInput
                                fieldType={config?.fieldType || 'text'}
                                options={config?.options || []}
                                value={currentVal}
                                label={config?.question}
                                questionTitle={config?.questionTitle}
                                onChange={(val) => {
                                  const updated = Array.isArray(editValue) ? [...editValue] : [];
                                  updated[idx] = val;
                                  setEditValue(updated);
                                }}
                              />
                            </Box>
                          </Box>
                      );
                    })}
                  </Box>
                  <ActionButtons onCancel={handleClose} onSave={handleSave} hasChanged={hasChanged} loading={submitLoading} />
                </Box>

              );
            }
            const config = getQuestionConfig(editQuestion.id);
            let options = config?.options || [];
            if (config?.fieldType === 'preferred_language' && typeof config?.getOptions === 'function' && languageOptions) {
              options = config.getOptions(languageOptions);
            }
            return (
              <Box sx={{ minWidth: { xs: 'unset', sm: 340 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {config?.question && (
                    <Typography varaiant='labelD' sx={{ color: '#333', fontSize: '13px !important', lineHeight: '20px !important', fontWeight: '700' }} component="legend">
                      {config?.question}
                      {config?.questionTitle && (
                        <Box component='span' sx={{ color: '#8C8C8C', ml: 0.5 }}>
                          {config?.questionTitle}
                        </Box>
                      )}
                    </Typography>
                  )}
                  <FieldInput
                    fieldType={config?.fieldType || 'text'}
                    options={options}
                    value={editValue}
                    onChange={setEditValue}
                    label={config?.question}
                    questionTitle={config?.questionTitle}
                  />
                </Box>
                <ActionButtons onCancel={handleClose} onSave={handleSave} hasChanged={hasChanged} loading={submitLoading} />
              </Box>
            );
          })()}
        </Box>
      </GlassModal>
    </Box>
  );
};

export default Profiler;